import React from "react";
import { useShallow } from "zustand/react/shallow";
import { Event, RoomPlayerState } from "@uno/protocol";
import { AppContext } from "context";
import { useSounds } from "context/SoundContext";
import useStoreRoom from "store/useStoreRoom";
import useStoreRoomPlayer from "store/useStoreRoomPlayer";
import useStoreRoomScene from "store/useStoreRoomScene";
import useStoreAnimation from "store/useStoreAnimation";
import FromRoomPlayerHandToDiscardWithoutRotationAnimation from "../../animation/FromRoomPlayerHandToDiscardWithoutRotationAnimation";

const PUT_ACK_TIMEOUT_MS = 3000;

let pendingPut: string | null = null;

const rollback = (cardId: string) => {
    const element = document.querySelector(`[data-card-id="${cardId}"]`);

    element?.getAnimations().forEach(animation => animation.cancel());
};

const usePlayCard = () => {
    const { socket } = React.useContext(AppContext);
    const { play } = useSounds();
    const roomId = useStoreRoom(state => state.room?.id);
    const playerId = useStoreRoomPlayer(useShallow(state => state.roomPlayer?.player?.id));
    const enqueue = useStoreAnimation(useShallow(state => state.enqueue));
    const atPosition = useStoreRoomScene(useShallow(state => state.position));
    const setHoveredCardId = useStoreRoomScene(useShallow(state => state.setHoveredCardId));

    return React.useCallback((cardId: string) => {
        setHoveredCardId(null);

        if(!socket?.connected || !roomId || !playerId || pendingPut) {
            return;
        }

        const roomPlayer = useStoreRoomPlayer.getState().roomPlayer;
        const card = useStoreRoomScene.getState().cards[playerId]?.find(x => x.id === cardId);
        const isMyTurn = !!roomPlayer && [RoomPlayerState.Active, RoomPlayerState.AwaitingAction].includes(roomPlayer.state) && !roomPlayer.hasAlreadyPutCard;

        if(!isMyTurn || !card?.canBePut) {
            return;
        }

        pendingPut = cardId;

        const timeout = setTimeout(() => {
            if(pendingPut === cardId) {
                pendingPut = null;
            }
        }, PUT_ACK_TIMEOUT_MS);

        const game = useStoreRoom.getState().room?.game;
        const position = (game?.discardOffset ?? 0) + (game?.discard.length ?? 0);
        const { initial, effects, transitions, duration, onAnimationEnd } = FromRoomPlayerHandToDiscardWithoutRotationAnimation(playerId, atPosition(playerId, cardId), position);

        enqueue(cardId, { name: "room-player-putting-card", initial, effects, transitions, duration, onAnimationEnd });
        play("putCard");

        socket.emit(Event.RoomPlayerPutCard, { roomId, cardId }, (result?: { success: boolean }) => {
            clearTimeout(timeout);
            pendingPut = null;

            if(!result?.success) {
                rollback(cardId);
            }
        });
    }, [socket, roomId, playerId, enqueue, atPosition, setHoveredCardId, play]);
};

export default usePlayCard;
